/**
 * Navigation logic for authentication flow
 */

import type { AuthPhase } from '@/types/profileTypes';
import { authPhaseConfigs } from '@/utils/auth-phase-config';
import { saveEnhancedAuthState } from '@/utils/auth-state-persistence';
import { features } from '@/config/features';
import type { AuthOrchestratorState } from './useAuthState';
import { NO_BACK_PHASES, ROLE_PHASES } from '../constants';
import { createDebugLogger } from '../utils/debug';
import { phaseAfterIdentityLink } from '../utils/identityLinkRouting';

const logger = createDebugLogger('AuthNavigation');

export function useAuthNavigation(
  state: AuthOrchestratorState,
  deps: {
    hasAlternateContact: () => boolean;
    completeProfileAndRedirect: () => void;
  }
) {
  /**
   * Validate phase transition is allowed
   */
  const validatePhaseTransition = (from: AuthPhase, to: AuthPhase): boolean => {
    // Can't transition to the same phase
    if (from === to) return false;

    // Can't transition from complete or error unless resetting
    if (from === 'complete' || from === 'error') {
      return to === 'idle' || to === 'contact';
    }

    // Can't transition to idle unless resetting
    // Note: At this point, 'from' is already narrowed to exclude 'complete' and 'error'
    // since those cases are handled above. Transitioning to 'idle' from any other phase
    // is not allowed (only complete/error can transition to idle for resetting).
    if (to === 'idle') {
      return false;
    }

    // Security phases should only transition through proper flow
    const securityPhases = ['otp', 'password', 'passwordSignup', 'setPassword'];
    if (securityPhases.includes(from) && securityPhases.includes(to)) {
      // Allow otp -> setPassword for new users
      if (from === 'otp' && to === 'setPassword') return true;
      // Otherwise security phases shouldn't transition to each other
      return false;
    }

    return true;
  };
  /**
   * Check if alternate contact phase is accessible
   */
  const isAlternateContactPhaseAccessible = (): boolean => {
    // Check if cross-linking feature is enabled
    if (!features.accountCrossLinking) {
      logger.log('Account cross-linking feature is disabled');
      return false;
    }

    // Can't go back to alternate contact if user already added one
    if (deps.hasAlternateContact()) {
      logger.log('User already has alternate contact, cannot go back');
      return false;
    }

    // Can't go back if user explicitly skipped
    if (state.alternateContactSkipped) {
      logger.log('User skipped alternate contact, cannot go back');
      return false;
    }

    // Can't go back if we've passed certain gates
    if (state.passedGates?.has('accountDiscovery') || state.passedGates?.has('identityConflict')) {
      logger.log('Passed critical gates, cannot go back to alternate contact');
      return false;
    }

    return true;
  };

  /**
   * Check if we can navigate to a target phase
   */
  const canNavigateTo = (
    targetPhase: AuthPhase,
    direction: 'forward' | 'back' = 'forward'
  ): boolean => {
    // Can't navigate to the same phase
    if (targetPhase === state.phase) return false;

    // Special handling for back navigation - more permissive
    if (direction === 'back') {
      // Prevent going back to authentication phases once completed
      if (targetPhase === 'otp' || targetPhase === 'password') {
        logger.log(`Cannot navigate back to ${targetPhase} after verification`);
        return false;
      }

      // Check if target is in completed phases (we've been there before)
      if (!state.completedPhases.has(targetPhase) && !state.phaseHistory.includes(targetPhase)) {
        // Special case for alternateContact
        if (targetPhase === 'alternateContact') {
          return isAlternateContactPhaseAccessible();
        }
        // Allow navigation to phases we haven't completed yet if going back
        // This handles session recovery cases
        return true;
      }

      // Allow going back to completed phases (except authentication phases)
      return true;
    }

    // Forward navigation rules

    // Check locked phases
    if (state.lockedPhases?.has(targetPhase)) {
      logger.log('Phase is locked:', targetPhase);
      return false;
    }

    // Check for one-way gates (like identityConflict)
    const config = authPhaseConfigs[targetPhase];
    if (config?.isOneWayGate && state.passedGates?.has(targetPhase)) {
      logger.log('Already passed one-way gate:', targetPhase);
      return false;
    }

    // Check if conflict was already resolved
    if (targetPhase === 'identityConflict' && state.resolvedConflicts?.size > 0) {
      logger.log('Identity conflict already resolved, cannot go back');
      return false;
    }

    // Check if phase is accessible (for forward navigation to alternateContact)
    if (targetPhase === 'alternateContact') {
      return isAlternateContactPhaseAccessible();
    }

    // Get phase config to check navigation rules
    if (!config) return false;

    // Check if phase allows back navigation
    if (!config.allowBackNavigation) return false;

    // Check for one-way gates - can't go back to them once passed
    if (config.isOneWayGate && state.encounteredPhases.has(targetPhase)) {
      // Already passed this one-way gate, can't go back
      return false;
    }

    return true;
  };

  /**
   * Navigate to a specific phase
   */
  const navigateToPhase = async (
    targetPhase: AuthPhase,
    direction: 'forward' | 'back' = 'forward'
  ): Promise<boolean> => {
    // Check if navigation is allowed
    if (!canNavigateTo(targetPhase, direction)) {
      logger.warn(`Cannot navigate to phase: ${targetPhase}`);
      return false;
    }

    // Validate the transition
    if (!validatePhaseTransition(state.phase, targetPhase)) {
      logger.warn(`Invalid phase transition from ${state.phase} to ${targetPhase}`);
      return false;
    }

    logger.log(`Navigating to phase: ${targetPhase} (${direction})`);

    // Clear any errors when navigating
    state.error = null;

    // Only store current phase in history when going forward
    // When going back, the history has already been popped in goBack()
    if (direction === 'forward' && !state.phaseHistory.includes(state.phase)) {
      state.phaseHistory.push(state.phase);
    }

    // Update phase
    state.previousPhase = state.phase;
    state.phase = targetPhase;

    // Mark as encountered
    state.encounteredPhases.add(targetPhase);

    // Mark as completed when moving forward
    if (direction === 'forward') {
      state.completedPhases.add(state.previousPhase);
    }

    // Save state for recovery
    try {
      await saveEnhancedAuthState(state);
    } catch (error) {
      logger.error('Failed to save state during navigation:', error);
    }

    return true;
  };

  /**
   * Check if back navigation is available from current phase
   */
  const canGoBack = (): boolean => {
    // First check if we have history
    if (state.phaseHistory.length > 0) {
      const previousPhase = state.phaseHistory[state.phaseHistory.length - 1];
      return canNavigateTo(previousPhase, 'back');
    }

    // Check fallback logic
    const currentPhase = state.phase;

    // These phases cannot go back
    if (NO_BACK_PHASES.includes(currentPhase)) {
      return false;
    }

    // Check specific phase logic
    switch (currentPhase) {
      case 'generalProfile':
        // Can only go back if alternate contact is accessible
        return isAlternateContactPhaseAccessible();
      case 'alternateContact':
        // Can't go back from alternateContact in most flows
        return false;
      case 'setPassword':
        // Can't go back from setPassword
        return false;
      case 'teacherProfile':
      case 'studentProfile':
      case 'parentProfile':
      case 'employerProfile':
      case 'schoolProfile':
      case 'organizerProfile':
        // First role can go back to generalProfile, others can go to previous role
        return true;
      default:
        return false;
    }
  };

  /**
   * Go back to previous phase
   */
  const goBack = async (): Promise<boolean> => {
    // First check if we can go back
    if (!canGoBack()) {
      logger.warn('Cannot go back from current phase:', state.phase);
      return false;
    }

    // Get the last phase from history
    if (state.phaseHistory.length > 0) {
      const previousPhase = state.phaseHistory[state.phaseHistory.length - 1];

      // Check if we can actually navigate to it
      if (canNavigateTo(previousPhase, 'back')) {
        // Remove the last entry from history
        state.phaseHistory.pop();

        // Navigate to the previous phase
        return navigateToPhase(previousPhase, 'back');
      }
    }

    // If no valid history, use specific fallback logic for role profiles only
    const currentPhase = state.phase;

    // Special handling for role profiles
    if (ROLE_PHASES.includes(currentPhase)) {
      if (state.currentRoleIndex === 0) {
        // First role, go back to generalProfile
        return navigateToPhase('generalProfile', 'back');
      }
      // Go to previous role
      const prevRoleIndex = state.currentRoleIndex - 1;
      const prevRole = state.selectedUserTypes[prevRoleIndex]?.toLowerCase();
      if (prevRole) {
        const targetPhase = `${prevRole}Profile` as AuthPhase;
        state.currentRoleIndex = prevRoleIndex;
        return navigateToPhase(targetPhase, 'back');
      }
    }

    // For generalProfile, only go back if alternate contact is accessible
    if (currentPhase === 'generalProfile' && isAlternateContactPhaseAccessible()) {
      return navigateToPhase('alternateContact', 'back');
    }

    logger.warn('No valid previous phase to navigate to');
    return false;
  };

  /**
   * Navigate to next phase in the flow
   */
  const nextPhase = async () => {
    // Determine the next phase based on current phase
    let targetPhase: AuthPhase | null = null;

    switch (state.phase) {
      case 'contact':
      case 'otp':
      case 'password':
        // These phases are handled by the auth flow system
        // Transitions are managed by useAuthCore after authentication
        logger.warn(`nextPhase called from ${state.phase}, which should be handled by auth flow`);
        return;
      case 'passwordSignup':
        // After password signup, go to alternate contact or skip to general profile
        targetPhase = features.accountCrossLinking ? 'alternateContact' : 'generalProfile';
        break;
      case 'setPassword':
        targetPhase = features.accountCrossLinking ? 'alternateContact' : 'generalProfile';
        break;
      case 'alternateContact':
        // After alternate contact, continue to general profile
        // Unless we discovered an existing account
        if (state.discoveredAccountId) {
          targetPhase = 'accountDiscovery';
        } else {
          targetPhase = 'generalProfile';
        }
        break;
      case 'accountDiscovery':
        // After account discovery, user has chosen how to proceed
        targetPhase = 'generalProfile';
        break;
      case 'generalProfile':
        // If we have pending identity linking, go to OTP phase
        if (state.pendingIdentityLink.type && state.pendingIdentityLink.otpSent) {
          targetPhase = 'identityLinkingOtp';
        } else {
          // 'None' / 'Just Joining' is a sentinel with no role-specific profile
          // phase — filter it out. If no real roles remain, complete.
          const realRoles = state.selectedUserTypes.filter(t => t !== 'None');
          if (realRoles.length > 0) {
            state.currentRoleIndex = 0;
            targetPhase = `${realRoles[0].toLowerCase()}Profile` as AuthPhase;
          } else {
            deps.completeProfileAndRedirect();
            return;
          }
        }
        break;
      case 'identityLinkingOtp':
      case 'identityConflict': {
        // Reached from "Add Mobile Phone" (before the profile step): go to the profile
        // step next. Reached after it: continue to role profiles (skip 'None') or finish.
        const route = phaseAfterIdentityLink({
          encounteredGeneralProfile: state.encounteredPhases.has('generalProfile'),
          discoveredAccountId: state.discoveredAccountId,
          realRoles: state.selectedUserTypes.filter(t => t !== 'None')
        });
        if ('complete' in route) {
          deps.completeProfileAndRedirect();
          return;
        }
        if (ROLE_PHASES.includes(route.phase)) state.currentRoleIndex = 0;
        targetPhase = route.phase;
        break;
      }

      case 'teacherProfile':
      case 'studentProfile':
      case 'parentProfile':
      case 'employerProfile':
      case 'schoolProfile':
      case 'organizerProfile': {
        // Advance through real roles only ('None' has no profile phase).
        const realRoles = state.selectedUserTypes.filter(t => t !== 'None');
        state.currentRoleIndex++;
        if (state.currentRoleIndex < realRoles.length) {
          const nextRole = realRoles[state.currentRoleIndex].toLowerCase();
          targetPhase = `${nextRole}Profile` as AuthPhase;
        } else {
          // All roles complete
          deps.completeProfileAndRedirect();
          return;
        }
        break;
      }
      default:
        logger.warn(`Unhandled phase in nextPhase: ${state.phase}`);
        return;
    }

    // Navigate to the target phase
    if (targetPhase) {
      await navigateToPhase(targetPhase, 'forward');
    }
  };

  /**
   * Navigate to previous phase (simpler version)
   */
  const previousPhase = () => {
    const temp = state.phase;
    state.phase = state.previousPhase;
    state.previousPhase = temp;
  };

  /**
   * Set phase directly (for special cases)
   */
  const setPhase = (phase: AuthPhase) => {
    // Add current phase to history before changing
    if (!state.phaseHistory.includes(state.phase)) {
      state.phaseHistory.push(state.phase);
    }

    // Mark as encountered
    state.encounteredPhases.add(phase);

    state.previousPhase = state.phase;
    state.phase = phase;
    // Clear any existing errors when changing phases
    state.error = null;
  };

  /**
   * Navigate to previous role
   */
  const navigateToPreviousRole = () => {
    if (state.currentRoleIndex > 0) {
      state.currentRoleIndex--;
      const previousRole = state.selectedUserTypes[state.currentRoleIndex].toLowerCase();
      state.phase = `${previousRole}Profile` as AuthPhase;
    } else if (state.phaseHistory.includes('generalProfile')) {
      // Go back to general profile if at first role
      state.phase = 'generalProfile';
    }
  };

  /**
   * Change contact and restart flow
   */
  const changeContact = () => {
    state.contact = '';
    state.phase = 'contact';
    state.error = null;
  };

  /**
   * Retry after error
   */
  const retryAfterError = () => {
    state.error = null;
    if (state.previousPhase && state.previousPhase !== 'error') {
      state.phase = state.previousPhase;
    } else {
      state.phase = 'contact';
    }
  };

  return {
    canNavigateTo,
    navigateToPhase,
    canGoBack,
    goBack,
    nextPhase,
    previousPhase,
    setPhase,
    navigateToPreviousRole,
    changeContact,
    retryAfterError,
    isAlternateContactPhaseAccessible
  };
}
